import { Component, signal, computed, OnInit, inject,
  ChangeDetectionStrategy, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { DataService } from '../../services/data.service';
import { AlertConfirmService } from '../../services/alert-confirm.service';

import * as XLSX from 'xlsx';

export interface ExcelImportResult {
  fileName: string;
  columns: string[];
  rows: Record<string, unknown>[];
  selectedColumns: string[];
}

@Component({
  selector: 'app-excel-import-export',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './excel-import-export.component.html',
  styleUrls: ['./excel-import-export.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExcelImportExportComponent {
  @Input() previewRows = 8;
  @Input() pageSize = 8;
  @Input() exportFormat: 'xlsx' | 'csv' = 'xlsx';
  @Input() exportFileName = 'exported-data';

  @Output() imported = new EventEmitter<ExcelImportResult>();
  @Output() exported = new EventEmitter<ExcelImportResult>();

  dataService     = inject(DataService);
  loading = false;
  allData: any = [];
  user: any = null;

  columns: string[] = [];
  selectedColumns: string[] = [];
  rows: Record<string, unknown>[] = [];

  currentPage = 1;
  totalPages = 1;
  isDragging = false;
  isProcessing = false;
  errorMessage = '';
  successMessage = '';
  fileName = '';
  tbl2ImpExp = '';
  tbls4ImpExp: any = [
    {"tblName": "admissions",       "tblPrint": "Admission"},
    {"tblName": "inquiries",        "tblPrint": "Inquries"},
    {"tblName": "fee_receipts",     "tblPrint": "Fees Receipts"},
    {"tblName": "lib_books",        "tblPrint": "Library Books"},
    {"tblName": "transport_routes", "tblPrint": "Transport Routs"},
    {"tblName": "exam_results",     "tblPrint": "Student Marks"},
    {"tblName": "master_data",      "tblPrint": "Master Data"}
  ];

  constructor(private alertService: AlertConfirmService) {
    //
  }

  async ngOnInit() {
    let erp_rem: any = localStorage.getItem("erp_rem");
    if(erp_rem) {
      erp_rem = JSON.parse(erp_rem);
      erp_rem = this.dataService.deCodeUser(erp_rem);
      this.user = erp_rem;
      // this.acd_session = this.user.session + '-' + ((parseInt(this.user.session) + 1) - 2000);
    }
  }


  get selectedRows(): Record<string, unknown>[] {
    return this.rows.map(row => {
      const output: Record<string, unknown> = {};
      for (const column of this.selectedColumns) {
        output[column] = row[column] ?? '';
      }
      return output;
    });
  }

  get paginatedRows(): Record<string, unknown>[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.selectedRows.slice(start, start + this.pageSize);
  }

  get startRow(): number {
    return this.rows.length ? (this.currentPage - 1) * this.pageSize + 1 : 0;
  }

  get endRow(): number {
    return Math.min(this.currentPage * this.pageSize, this.rows.length);
  }

  get allSelected(): boolean {
    return this.columns.length > 0 && this.selectedColumns.length === this.columns.length;
  }

  get someSelected(): boolean {
    return this.selectedColumns.length > 0 && !this.allSelected;
  }

  get canExport(): boolean {
    return this.rows.length > 0 && this.selectedColumns.length > 0 && !this.isProcessing;
  }

  onFileInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) this.handleFile(file);
    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) this.handleFile(file);
  }

  toggleColumn(column: string, checked: boolean): void {
    this.selectedColumns = checked
      ? [...this.selectedColumns, column]
      : this.selectedColumns.filter(c => c !== column);

    this.selectedColumns = this.columns.filter(c => this.selectedColumns.includes(c));
    this.recalculatePagination();
  }

  toggleAll(checked: boolean): void {
    this.selectedColumns = checked ? [...this.columns] : [];
    this.recalculatePagination();
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  clearData(): void {
    this.columns = [];
    this.selectedColumns = [];
    this.rows = [];
    this.fileName = '';
    this.currentPage = 1;
    this.totalPages = 1;
    this.errorMessage = '';
    this.successMessage = '';
  }

  exportData(): void {
    if (!this.canExport) {
      this.errorMessage = this.rows.length
        ? 'Select at least one column before exporting.'
        : 'Import a file before exporting.';
      return;
    }

    try {
      const exportRows = this.selectedRows;
      const worksheet = XLSX.utils.json_to_sheet(exportRows, {
        header: this.selectedColumns
      });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');

      const extension = this.exportFormat === 'csv' ? 'csv' : 'xlsx';
      const safeName = this.exportFileName.replace(/\.[^.]+$/, '') || 'exported-data';
      XLSX.writeFile(workbook, `${safeName}.${extension}`, {
        bookType: this.exportFormat
      });

      this.successMessage = `Exported ${exportRows.length} row(s) with ${this.selectedColumns.length} column(s).`;
      this.exported.emit({
        fileName: this.fileName,
        columns: this.columns,
        rows: this.rows,
        selectedColumns: this.selectedColumns
      });
    } catch {
      this.errorMessage = 'The data could not be exported. Please try again.';
    }
  }

  private handleFile(file: File): void {
    this.resetMessages();

    const extension = file.name.toLowerCase().split('.').pop();
    if (!extension || !['xlsx', 'csv'].includes(extension)) {
      this.errorMessage = 'Unsupported file type. Please upload an .xlsx or .csv file.';
      return;
    }

    if (file.size === 0) {
      this.errorMessage = 'The selected file is empty.';
      return;
    }

    this.isProcessing = true;
    this.fileName = file.name;

    const reader = new FileReader();

    reader.onload = () => {
      try {
        const result = reader.result;
        if (!result) throw new Error('Empty file content.');

        const workbook = XLSX.read(result, {
          type: 'array',
          cellDates: true,
          raw: false
        });

        const firstSheet = workbook.SheetNames[0];
        if (!firstSheet) throw new Error('No worksheet was found.');

        const worksheet = workbook.Sheets[firstSheet];
        const matrix = XLSX.utils.sheet_to_json<unknown[]>(worksheet, {
          header: 1,
          defval: ''
        });

        if (!matrix.length) throw new Error('The worksheet contains no data.');

        const headerRow = (matrix[0] ?? []).map(value => String(value ?? '').trim());

        if (!headerRow.some(Boolean)) {
          throw new Error('The file does not contain a valid header row.');
        }

        const normalizedHeaders = this.makeUniqueHeaders(headerRow);
        const dataRows = matrix.slice(1)
          .filter(row => row.some(value => String(value ?? '').trim() !== ''))
          .map((row, index) => {
            if (row.length > normalizedHeaders.length) {
              throw new Error(
                `Row ${index + 2} contains more cells than the header row.`
              );
            }

            const record: Record<string, unknown> = {};
            normalizedHeaders.forEach((header, colIndex) => {
              record[header] = row[colIndex] ?? '';
            });
            return record;
          });

        if (!dataRows.length) {
          throw new Error('The file contains headers but no data rows.');
        }

        this.columns = normalizedHeaders;
        this.selectedColumns = [...normalizedHeaders];
        this.rows = dataRows;
        this.currentPage = 1;
        this.recalculatePagination();
        this.successMessage = `Imported ${dataRows.length} row(s) and ${normalizedHeaders.length} column(s).`;

        this.imported.emit({
          fileName: file.name,
          columns: this.columns,
          rows: this.rows,
          selectedColumns: this.selectedColumns
        });
      } catch (error) {
        this.columns = [];
        this.selectedColumns = [];
        this.rows = [];
        this.currentPage = 1;
        this.totalPages = 1;
        this.errorMessage =
          error instanceof Error
            ? error.message
            : 'The file could not be parsed. It may be corrupted or malformed.';
      } finally {
        this.isProcessing = false;
      }
    };

    reader.onerror = () => {
      this.isProcessing = false;
      this.errorMessage = 'The file could not be read.';
    };

    reader.readAsArrayBuffer(file);
  }

  private makeUniqueHeaders(headers: string[]): string[] {
    const used = new Map<string, number>();

    return headers.map((header, index) => {
      const base = header || `Column ${index + 1}`;
      const count = used.get(base) ?? 0;
      used.set(base, count + 1);
      return count === 0 ? base : `${base} (${count + 1})`;
    });
  }

  private recalculatePagination(): void {
    this.totalPages = Math.max(1, Math.ceil(this.rows.length / this.pageSize));
    if (this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages;
    }
  }

  private resetMessages(): void {
    this.errorMessage = '';
    this.successMessage = '';
  }

  async sampleDownload() {
    this.resetMessages();
    this.loading = true;
    await this.dataService.getSingleRecord('adm_fldonly', this.tbl2ImpExp).then((res: any) => {
      if(res && res.columns && res.columns.length) {
        const exportRows: any = [];
        const worksheet = XLSX.utils.json_to_sheet(exportRows, {
          header: res.columns
        });
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');

        const extension = 'xlsx';
        // const safeName = this.exportFileName.replace(/\.[^.]+$/, '') || 'exported-data';
        const safeName = this.tbl2ImpExp;
        XLSX.writeFile(workbook, `${safeName}.${extension}`, {
          bookType: extension
        });

        this.successMessage = `Exported ${exportRows.length} row(s) with ${res.columns.length} column(s).`;
        this.exported.emit({
          fileName: this.tbl2ImpExp + '.xlsx',
          columns: res.columns,
          rows: [],
          selectedColumns: res.columns
        });
      }
      this.loading = false;
    });

  }

  doImportData() {
    const myRecords = Object.assign([], this.rows);

    // Filter only visible columns in the data records
    const filteredRecords = myRecords.map((record: any) => {
      return this.selectedColumns.reduce((acc, key) => {
        let acc2: any = acc;
        if (key in record) {
          acc2[key] = record[key];
        }
        return acc;
      }, {});
    });    

    this.doSave('std_adm_bulk', filteredRecords);
  }

  // ── ACTIONS ─────────────────────────────────────────
  doSave(api_name: string, frmData: any) {
    this.loading = true;
    // this.submitSuccess = true;
    this.dataService.addNewRecord(api_name, frmData).subscribe({
      next: (response) => {
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doUpdate(api_name: string, frmData: any) {
    this.loading = true;
    // this.submitSuccess = true;
		const myid = frmData.id;
		delete frmData.id;

    this.dataService.updateRecord(api_name, myid, frmData).subscribe({
      next: (response) => {
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doDelete(api_name: string, myid: any) {
		this.loading = true;
    this.dataService.deleteRecord(api_name, myid).subscribe({
      next: (response) => {
        console.log('Response received in component:', response);
        // You can handle your loading states or form resets here:
        this.loading = false;
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
	}

  confirmSessionUpgrade() {
    // location.href= '/dashboard/230b0108';
    // return;
    // if (!confirm('Sure to Upgrade Session from '+this.user.session+' to '+(parseInt(this.user.session) + 1)+'?')) return;
    this.alertService.confirm(
      'Session Upgrade',
      'Are you sure to Upgrade Session from '+this.user.session+' to '+(parseInt(this.user.session) + 1)+'?',
      () => {
        // User confirmed
        this.sessionUpgrade();
      },
      () => {
        location.href= '/dashboard/230b0108';
        // User cancelled
        return;
      },
      'danger'
    );
  }

  sessionUpgrade() {
    const newDbInfo: any = {
      erp_key: this.user.erp_key,
      db_name: 'erpdb_' + this.user.erp_key.split('-')[0], 
      schema_name: 'inst_' + this.user.erp_key.split('-')[1] + '_' + (parseInt(this.user.session) + 1),
      gen_pub: false
    }

    this.dataService.addNewRecord('new-schema', newDbInfo, true).subscribe({
      next: (response) => {
        if(response.success) {
          location.href= '/dashboard/230b0108';
        }
      },
      error: (error) => {
        console.error('Error received in new DB: ', error?.message);
        location.href= '/dashboard/230b0108';
      }
    });

  }
}
